import React from 'react';
import { FlatList, type ListRenderItemInfo } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { VStack, theme } from '@squire/ui';

import { SearchResultCard } from '@/components/home/SearchResultCard';
import { SearchResultCardSkeleton } from '@/components/home/SearchResultCardSkeleton';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { openDirections } from '@/utils/openDirections';

import { EmptySearchState } from '../EmptySearchState';
import { styles } from './styles';
import type { SearchListShop, SearchResultsListProps } from './types';

const SearchResultsList = ({
  shops,
  loading,
  onResultPress,
  header,
  contentContainerStyle,
  hasSearched = false,
  emptyVariant = 'search',
  prioritizeAlias = false,
}: SearchResultsListProps) => {
  const { i18n } = useScreenTranslations('HomeScreen');

  const handleDirectionsPress = (result: SearchListShop) => {
    const [latitude, longitude] = result.location.coordinates;

    return openDirections({
      latitude,
      longitude,
      locale: i18n.language,
      name: result.name,
      googlePlaceId: result.googlePlaceId,
    });
  };

  const renderSearchResultItem = ({
    item,
    index,
  }: ListRenderItemInfo<SearchListShop>) => (
    <Animated.View entering={index < 10 ? FadeIn.delay(index * 50) : undefined}>
      <SearchResultCard
        key={item.id}
        testID={`search-result-card-${index}`}
        shop={item}
        onPress={() => onResultPress(item)}
        onDirectionsPress={() => handleDirectionsPress(item)}
        prioritizeAlias={prioritizeAlias}
      />
    </Animated.View>
  );

  return (
    <FlatList
      data={loading ? [] : shops}
      contentContainerStyle={[styles.list, contentContainerStyle]}
      keyExtractor={(item) => item.id}
      renderItem={renderSearchResultItem}
      showsVerticalScrollIndicator={false}
      ListEmptyComponent={
        loading ? (
          <VStack space={theme.spacing[5]}>
            {Array.from({ length: 6 }).map((_, index) => (
              <SearchResultCardSkeleton key={index} />
            ))}
          </VStack>
        ) : (
          <EmptySearchState hasSearched={hasSearched} variant={emptyVariant} />
        )
      }
      ListHeaderComponent={() => header}
    />
  );
};

SearchResultsList.displayName = 'SearchResultsList';
export { SearchResultsList };
